import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import {
    useGetDistributorCompaniesQuery,
    useGetDistributorSubscribersDataQuery,
    useGetInvestmentFundsQuery,
} from 'api/main';
import { distributorSchemas } from 'schemas';
import {
    customTotalParser,
    parseCustomData,
    pieChartOptions,
    subscriptionNumberFormatter,
} from '../admin-distributors/AdminDistributorDashboard';
import { ORDER_BY } from 'utils/constants';
import { parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ChartContainer } from '../common-components';
import { CustomSelect, Loader } from 'components';
import { Box, MenuItem, Typography } from '@mui/material';

const SubscriptionsByPartner = ({ status, onListFilter, type, classes }) => {
    const { data: fundList = [] } = useGetInvestmentFundsQuery({ visible: 1 });

    const [selectedFund, setSelectedFund] = useState('all');
    const [selectedDistributor, setSelectedDistributor] = useState('all');
    const [selectedDistributorCompany, setSelectedDistributorCompany] = useState('all');

    const { data: dataByCIF = {}, isFetching: dataByCIFLoader } =
        useGetDistributorSubscribersDataQuery({
            cifId: selectedDistributor === 'all' ? undefined : selectedDistributor,
            subscriptionStatus: status,
        });

    // used for charts that show data by distributors
    const filteredFundList = useMemo(
        () => fundList.filter(f => !!dataByCIF?.[f.id]),
        [fundList, dataByCIF]
    );
    const { data: { distributors = [] } = { distributors: [] }, isFetching: distributorLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                orderBy: ORDER_BY.distributorCompanyName,
                oneOrMoreConventionsFullySigned: true,
                conventionWithFund: filteredFundList.map(f => f.id),
            })
        );
    const filteredDistributors = useMemo(
        () =>
            distributors.filter(
                d =>
                    selectedFund === 'all' ||
                    d[0].conventions?.find(c => c.investmentFund.id === selectedFund)
            ),
        [distributors, selectedFund]
    );
    const totalValueByFund = useMemo(
        () => customTotalParser(dataByCIF, selectedFund, 'sum_indirect_subscriptions'),
        [dataByCIF, selectedFund]
    );
    const totalNumberByFund = useMemo(
        () => customTotalParser(dataByCIF, selectedFund, 'indirect_subscriptions'),
        [dataByCIF, selectedFund]
    );

    const chartLabels = useMemo(
        () =>
            selectedFund === 'all'
                ? filteredFundList.map(f => f.name)
                : Object.keys(dataByCIF?.[selectedFund] || {}).map(k => k || '-'),
        [selectedFund, filteredFundList, dataByCIF]
    );

    return (
        <Box className={classes.infoContainer}>
            <Typography variant="body1" gutterBottom className={classes.subtitle}>
                {texts.SUBSCRIPTIONS_BY_PARTNER}
            </Typography>
            <Loader
                loading={dataByCIFLoader || !dataByCIF || distributorLoader}
                height="350px"
                marginTop={0}
            >
                <Box className={classes.subsection}>
                    <Box display="flex" alignItems="center" gap="15px">
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            classes={{ root: classes.whiteBackground }}
                            value={selectedDistributor}
                            label={texts.PARTNERS}
                            onChange={e =>
                                e.target.value === 'all' && setSelectedDistributor(e.target.value)
                            }
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredDistributors.map(({ 0: { id, user, corporateName } }) => (
                                <MenuItem
                                    key={id}
                                    value={user.id}
                                    onClick={() => {
                                        user && setSelectedDistributor(user.id);
                                        user && setSelectedDistributorCompany(id);
                                    }}
                                >
                                    {corporateName}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            classes={{ root: classes.whiteBackground }}
                            value={selectedFund}
                            label={texts.PRODUIT}
                            onChange={e => setSelectedFund(e.target.value)}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredFundList
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map(f => (
                                    <MenuItem key={f.id} value={f.id}>
                                        {f.name}
                                    </MenuItem>
                                ))}
                        </CustomSelect>
                    </Box>
                    {totalValueByFund === 0 && totalNumberByFund === 0 ? (
                        <Typography variant="subtitle1" align="center" gutterBottom>
                            {texts.NO_DATA_AVAILABLE}
                        </Typography>
                    ) : (
                        <Box display="flex" alignItems="center" height="350px" gap="12px">
                            <ChartContainer
                                useSymbol
                                title={texts.VALUE}
                                number={totalValueByFund}
                                handleClick={onListFilter(
                                    {
                                        product:
                                            selectedFund === 'all' ? undefined : [selectedFund],
                                        companyId:
                                            selectedDistributorCompany === 'all'
                                                ? undefined
                                                : [selectedDistributorCompany],
                                        indirectSubscriptions:
                                            selectedDistributorCompany === 'all' ? 1 : undefined,
                                    },
                                    type === 'completed' ? 'investors' : 'subscriptions'
                                )}
                                subtitleProps={{
                                    selectedFund: selectedFund,
                                    subType: 'indirect',
                                    valueType: 'value',
                                }}
                            >
                                <Chart
                                    type="pie"
                                    series={parseCustomData(
                                        dataByCIF,
                                        selectedFund,
                                        'sum_indirect_subscriptions'
                                    )}
                                    options={{
                                        ...pieChartOptions,
                                        tooltip: {
                                            ...pieChartOptions.tooltip,
                                            y: { formatter: val => parseSum(val) },
                                        },
                                        labels: chartLabels,
                                    }}
                                />
                            </ChartContainer>
                            <ChartContainer
                                title={texts.NUMBER}
                                number={totalNumberByFund}
                                subtitleProps={{
                                    selectedFund: selectedFund,
                                    subType: 'indirect',
                                    valueType: 'number',
                                }}
                                handleClick={onListFilter(
                                    {
                                        product:
                                            selectedFund === 'all' ? undefined : [selectedFund],
                                        companyId:
                                            selectedDistributorCompany === 'all'
                                                ? undefined
                                                : [selectedDistributorCompany],
                                    },
                                    type === 'completed' ? 'investors' : 'subscriptions'
                                )}
                            >
                                <Chart
                                    type="pie"
                                    series={parseCustomData(
                                        dataByCIF,
                                        selectedFund,
                                        'indirect_subscriptions'
                                    )}
                                    options={{
                                        ...pieChartOptions,
                                        tooltip: {
                                            ...pieChartOptions.tooltip,
                                            y: {
                                                formatter: subscriptionNumberFormatter,
                                            },
                                        },
                                        labels: chartLabels,
                                    }}
                                />
                            </ChartContainer>
                        </Box>
                    )}
                </Box>
            </Loader>
        </Box>
    );
};

export default SubscriptionsByPartner;
